// The Resource limits page's two tables (D83): every role with its override or "Uses the
// default", and the people who have a personal limit. Edit controls only for an admin with
// ManageResourceLimits.
import { PencilSimple, Trash } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { ActionIcon, Button } from "rizzui";

import { useT } from "../../i18n";
import { type Column, DataTable } from "../../ui/DataTable";
import { type Size, toGib, type UserLimit } from "./api";

export interface RoleRow {
  id: string;
  name: string;
  limit?: Size;
}

function SizeText({ size }: { size: Size }) {
  const t = useT().limits;
  return <span className="tabular whitespace-nowrap font-medium text-gray-900">{t.size(size.cpus, toGib(size.memory_bytes))}</span>;
}

/** Below `sm` the Limit column folds under the name, so the actions stay on screen. */
function FoldedLimit({ children }: { children: ReactNode }) {
  return <div className="mt-0.5 text-xs sm:hidden">{children}</div>;
}

function RowActions({ name, has, onEdit, onRemove }: { name: string; has: boolean; onEdit: () => void; onRemove: () => void }) {
  const t = useT().limits.admin;
  if (!has) {
    return (
      <Button size="sm" variant="outline" aria-label={t.setLabel(name)} onClick={onEdit}>
        {t.set}
      </Button>
    );
  }
  return (
    <div className="flex justify-end gap-2">
      <ActionIcon size="sm" variant="outline" aria-label={t.editLabel(name)} onClick={onEdit}>
        <PencilSimple weight="bold" className="h-4 w-4" />
      </ActionIcon>
      <ActionIcon size="sm" variant="outline" aria-label={t.removeLabel(name)} className="hover:border-red hover:text-red" onClick={onRemove}>
        <Trash weight="bold" className="h-4 w-4" />
      </ActionIcon>
    </div>
  );
}

export function RolesTable({
  rows,
  manage,
  onEdit,
  onRemove,
}: {
  rows: RoleRow[];
  manage: boolean;
  onEdit: (row: RoleRow) => void;
  onRemove: (row: RoleRow) => void;
}) {
  const t = useT().limits.admin;
  const limitText = (r: RoleRow) => (r.limit ? <SizeText size={r.limit} /> : <span className="text-gray-500">{t.usesDefault}</span>);
  const columns: Column<RoleRow>[] = [
    {
      key: "role",
      header: t.columns.role,
      sortValue: (r) => r.name,
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-gray-900">{r.name}</div>
          <FoldedLimit>{limitText(r)}</FoldedLimit>
        </div>
      ),
    },
    { key: "limit", header: t.columns.limit, hideBelow: "sm", cell: limitText },
  ];
  if (manage) {
    columns.push({
      key: "actions",
      header: "",
      className: "text-end",
      cell: (r) => <RowActions name={r.name} has={r.limit !== undefined} onEdit={() => onEdit(r)} onRemove={() => onRemove(r)} />,
    });
  }
  return <DataTable label={t.rolesTitle} columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

export function UsersTable({
  rows,
  manage,
  onEdit,
  onRemove,
}: {
  rows: UserLimit[];
  manage: boolean;
  onEdit: (row: UserLimit) => void;
  onRemove: (row: UserLimit) => void;
}) {
  const t = useT().limits.admin;
  const name = (u: UserLimit) => u.display_name || u.email;
  const columns: Column<UserLimit>[] = [
    {
      key: "user",
      header: t.columns.user,
      sortValue: name,
      cell: (u) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-gray-900">{name(u)}</div>
          {u.display_name ? <div className="truncate text-xs text-gray-500">{u.email}</div> : null}
          <FoldedLimit>
            <SizeText size={u} />
          </FoldedLimit>
        </div>
      ),
    },
    { key: "limit", header: t.columns.limit, hideBelow: "sm", cell: (u) => <SizeText size={u} /> },
  ];
  if (manage) {
    columns.push({
      key: "actions",
      header: "",
      className: "text-end",
      cell: (u) => <RowActions name={name(u)} has onEdit={() => onEdit(u)} onRemove={() => onRemove(u)} />,
    });
  }
  return <DataTable label={t.usersTitle} columns={columns} rows={rows} rowKey={(u) => u.user_id} />;
}
